import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { SearchFilters, Genre } from '../types';
import { getMovieGenres, getTVGenres } from '../api/tmdb';

interface SearchBarProps {
  onSearch?: (filters: SearchFilters) => void;
  showFilters?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, showFilters = true }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State for search and filters
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState<SearchFilters>({
    query: query,
    genre: null,
    year: null,
    rating: null,
    type: 'all'
  });
  
  // State for genres
  const [movieGenres, setMovieGenres] = useState<Genre[]>([]);
  const [tvGenres, setTVGenres] = useState<Genre[]>([]);

  // Load genres on component mount
  useEffect(() => {
    const loadGenres = async () => {
      try {
        const [movieGenresData, tvGenresData] = await Promise.all([
          getMovieGenres(),
          getTVGenres()
        ]);
        setMovieGenres(movieGenresData.genres);
        setTVGenres(tvGenresData.genres);
      } catch (error) {
        console.error('Error loading genres:', error);
      }
    };
    loadGenres();
  }, []);

  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      const newFilters = { ...filters, query: query.trim() };
      setFilters(newFilters);
      
      // Update URL params
      const params = new URLSearchParams();
      params.set('q', query.trim());
      if (newFilters.genre) params.set('genre', newFilters.genre.toString());
      if (newFilters.year) params.set('year', newFilters.year.toString());
      if (newFilters.rating) params.set('rating', newFilters.rating.toString());
      if (newFilters.type !== 'all') params.set('type', newFilters.type);
      
      setSearchParams(params);
      
      // Call onSearch callback if provided
      if (onSearch) {
        onSearch(newFilters);
      } else {
        // Navigate to search page
        navigate(`/search?${params.toString()}`);
      }
    }
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.query) params.set('q', newFilters.query);
    if (newFilters.genre) params.set('genre', newFilters.genre.toString());
    if (newFilters.year) params.set('year', newFilters.year.toString());
    if (newFilters.rating) params.set('rating', newFilters.rating.toString());
    if (newFilters.type !== 'all') params.set('type', newFilters.type);
    
    setSearchParams(params);
    
    // Call onSearch callback if provided
    if (onSearch) {
      onSearch(newFilters);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    const newFilters: SearchFilters = {
      query: query,
      genre: null,
      year: null,
      rating: null,
      type: 'all'
    };
    setFilters(newFilters);
    setSearchParams({ q: query });
    
    if (onSearch) {
      onSearch(newFilters);
    }
  };

  // Get all genres (combined)
  const allGenres = [...movieGenres, ...tvGenres];
  const uniqueGenres = allGenres.filter((genre, index, self) => 
    index === self.findIndex(g => g.id === genre.id)
  );

  return (
    <div className="w-full flex justify-center">
      {/* Search Form */}
      <form
        onSubmit={handleSearch}
        className="relative w-full max-w-2xl flex items-center bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-md border border-dark-700 shadow-xl rounded-full px-2 py-1"
        style={{ boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)' }}
      >
        <div className="relative flex-1 flex items-center">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center">
            <Search className="h-5 w-5 text-primary-400" />
          </span>
          <input
            type="text"
            placeholder="Search movies & series..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-12 pr-4 h-12 text-base bg-transparent border-none outline-none text-white placeholder:text-primary-300 rounded-full focus:ring-2 focus:ring-primary-500 transition-all"
            style={{ minWidth: 0 }}
          />
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          type="submit"
          className="ml-2 h-12 px-6 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary-400"
          style={{ minWidth: 0 }}
        >
          Search
        </motion.button>
      </form>

      {/* Filter Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-4 p-4 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg shadow-xl absolute left-0 right-0 z-20"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Media Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
              >
                <option value="all">All</option>
                <option value="movie">Movies</option>
                <option value="tv">TV Series</option>
              </select>
            </div>

            {/* Genre Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Genre
              </label>
              <select
                value={filters.genre || ''}
                onChange={(e) => handleFilterChange('genre', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
              >
                <option value="">All Genres</option>
                {uniqueGenres.map((genre) => (
                  <option key={genre.id} value={genre.id}>
                    {genre.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Year Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Year
              </label>
              <select
                value={filters.year || ''}
                onChange={(e) => handleFilterChange('year', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
              >
                <option value="">All Years</option>
                {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Rating Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Min Rating
              </label>
              <select
                value={filters.rating || ''}
                onChange={(e) => handleFilterChange('rating', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-3 py-2 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
              >
                <option value="">Any Rating</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((rating) => (
                  <option key={rating} value={rating}>
                    {rating}+ Stars
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(filters.genre || filters.year || filters.rating || filters.type !== 'all') && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
              >
                <span>Clear Filters</span>
              </button>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default SearchBar; 